import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download, FileText, ArrowRight, CheckCircle2 } from 'lucide-react';

export const PlaybookPromo: React.FC = () => {
  const highlights = [
    'Evidence-based forecasting framework',
    'Pipeline hygiene gates that actually work',
    'The 21-day rule for stalled deals',
    '90-day implementation path for CROs',
  ];

  return (
    <section className="w-full py-20 md:py-28 bg-gradient-to-br from-primary/5 via-background to-accent/5 border-y border-border">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mx-auto max-w-6xl"
        >
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center bg-card border border-border rounded-3xl p-8 md:p-12 shadow-elegant">
            {/* Left: Cover mockup + badge */}
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/10 rounded-2xl blur-3xl opacity-60" />
              <div className="relative w-full max-w-sm aspect-[3/4] bg-gradient-to-br from-[#0a1838] via-[#0f2350] to-[#0a1838] rounded-2xl shadow-2xl overflow-hidden border border-primary/20">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary" />
                <div className="flex flex-col h-full p-8 text-white">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 mb-auto">
                    <FileText className="w-3.5 h-3.5" />
                    NoQ5 · Playbook
                  </div>
                  <div className="space-y-3">
                    <div className="w-12 h-0.5 bg-primary" />
                    <h3 className="text-2xl md:text-3xl font-serif font-bold leading-tight">
                      The CRO Pipeline Playbook
                    </h3>
                    <p className="text-5xl font-serif font-bold text-primary">2026</p>
                    <p className="text-sm text-white/70 pt-4 border-t border-white/10">
                      How top revenue leaders forecast with evidence, not opinions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Copy + CTA */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full mb-4">
                <Download className="w-3.5 h-3.5" />
                FREE PLAYBOOK · 15 PAGES
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                The CRO Pipeline Playbook <span className="text-primary">2026</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                The strategic guide top revenue leaders use to fix pipeline hygiene, forecast with evidence, and run weekly reviews that boards trust.
              </p>

              <ul className="space-y-2.5 mb-8">
                {highlights.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm md:text-base">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/resources/cro-pipeline-playbook">
                  <Button size="lg" className="w-full sm:w-auto group">
                    <Download className="mr-2 w-4 h-4" />
                    Get the playbook
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <p className="text-xs text-muted-foreground self-center">
                  Sent to your inbox · No credit card
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
